/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import { useContext, useEffect, useMemo, useState } from "react";
import { Drawer as MuiDrawer, styled, IconButton, Box, Typography, Tooltip, useTheme } from "@mui/material";
import {
  StorageRounded,
  SubtitlesOutlined,
  TuneRounded,
  PeopleAltRounded,
  BusinessRounded,
  ChevronLeft,
  CloudUpload,
  MenuBook,
  Help,
  FactCheckRounded,
} from "@mui/icons-material";
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import NewReleasesRoundedIcon from '@mui/icons-material/NewReleasesRounded';
import { SidebarContext } from "./context/sidebar.context";
import useAuthorized from "../../../hooks/use-authorized.hook";
import SidebarLinks from "./sidebar-links";
import RouteEnum from "../../../router/enums/route.enum";
import { preloadParsedFiles } from "../../../router/routes";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getCounts } from "../../../store/actions/auth";
import { useDispatch, useSelector } from "react-redux";
import { selectCounts, selectAuthUser } from "../../../store/selectors";
import useSubscription from "../../../hooks/use-subscription.hook";
import UserMenu from "../header/user-menu";
import ThemeSwitcher from "../header/theme-switcher";
import SupportPopup from "./support-popup";

/* ─── Shared sidebar dimensions ─── */
export const DRAWER_WIDTH_EXPANDED = 180;
export const DRAWER_WIDTH_COLLAPSED = 64;
export const FOOTER_HEIGHT = 36;

/* ─── Tooltip posizionamento uniforme (sidebar chiusa) ─── */
const SIDEBAR_CLOSED_TOOLTIP_SLOT_PROPS = {
  popper: {
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  },
};

/* ─── Glass material ─── */
const glassBackground = (isDark) =>
  isDark ? "rgba(20, 20, 28, 0.82)" : "rgba(245, 245, 248, 0.92)";

const glassShadow = (isDark) =>
  isDark
    ? "0 4px 24px rgba(0, 0, 0, 0.3)"
    : "0 4px 24px rgba(0, 0, 0, 0.06)";

const glassStyle = (theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    backgroundColor: glassBackground(isDark),
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderRight: 0,
    boxShadow: glassShadow(isDark),
  };
};

/* ─── Mixins ─── */
const openedMixin = (theme) => ({
  ...glassStyle(theme),
  width: DRAWER_WIDTH_EXPANDED,
  position: "fixed",
  top: 0,
  left: 0,
  height: "100vh",
  transition: theme.transitions.create("width", {
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
    duration: 400,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  ...glassStyle(theme),
  width: DRAWER_WIDTH_COLLAPSED,
  position: "fixed",
  top: 0,
  left: 0,
  height: "100vh",
  transition: theme.transitions.create("width", {
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
    duration: 400,
  }),
  overflowX: "hidden",
});

/* ─── Styled Drawer ─── */
const drawerStyles = ({ theme, open }) => ({
  width: open ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  overflowX: "hidden",
  "& .MuiDrawer-paper": {
    overflowX: "hidden",
  },
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
});
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(
  // @ts-ignore - MUI styled with custom 'open' prop
  drawerStyles
);

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ...glassStyle(theme),
  padding: theme.spacing(1.6, 2.4),
  paddingBottom: 30,
  ...theme.mixins.toolbar,
  minHeight: 58,
  "@media (min-width:600px)": { minHeight: 58 },
  position: "relative",
}));

/* ─── Helpers ─── */
const iconColor = (theme) =>
  theme.palette.mode === "dark" ? "#ffffff" : theme.palette.text.primary;

/* ─── Sidebar Component ─── */
const Sidebar = () => {
  const theme = useTheme();
  const sidebar = useContext(SidebarContext);
  const counts = useSelector(selectCounts);
  const authUser = useSelector(selectAuthUser);
  const isAuthorised = useAuthorized();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasActiveSubscription, isLoading: isLoadingSubscription } =
    useSubscription();
  const [supportPopupOpen, setSupportPopupOpen] = useState(false);
  const [footerHovered, setFooterHovered] = useState(false);

  const isDark = theme.palette.mode === "dark";
  const userRole = authUser?.role || "";
  const isAdmin = userRole === "admin";
  const isViewer = userRole === "viewer";
  const isAuditor = userRole === "auditor";
  const isNewUser = authUser && authUser.role === null;

  useEffect(() => {
    dispatch(getCounts());
  }, [location, dispatch]);

  const archiveLink = useMemo(
    () => ({
      label: t("files-history.title"),
      link: RouteEnum.PARSED_FILES_VIEW_HISTORY,
      Icon: StorageRounded,
      count: counts.history_counter,
      requiresSubscription: true,
    }),
    [t, counts.history_counter]
  );

  const auditLink = useMemo(
    () => ({
      label: t("audit.sidebarLabel", "Audit"),
      link: RouteEnum.AUDIT,
      Icon: FactCheckRounded,
      requiresSubscription: false,
      premiumTooltip: t("audit.premium.title", "Premium Service"),
    }),
    [t]
  );

  const sidebarLinks = useMemo(
    () =>
      isAuditor
        ? [auditLink]
        : isViewer
        ? [archiveLink]
        : [
          ...(isAdmin || isNewUser
            ? [
              {
                label: "Company",
                link: RouteEnum.COMPANY,
                Icon: BusinessRounded,
                requiresSubscription: false,
              },
              auditLink,
              ...(isAdmin
                ? [{
                    label: "Analytics",
                    link: RouteEnum.ANALYTICS,
                    Icon: BarChartRoundedIcon,
                    requiresSubscription: false,
                  }]
                : []),
              // {
              //   label: t("sidebar.whats_new"),
              //   link: RouteEnum.WHATS_NEW,
              //   Icon: NewReleasesRoundedIcon,
              //   requiresSubscription: true,
              //   count: "NEW",
              // },
            ]
            : []),
          {
            label: t("uploading-files.title"),
            link: RouteEnum.UPLOADING_FILES,
            Icon: CloudUpload,
            count: counts.created_counter,
            requiresSubscription: true,
          },
          {
            label: t("parsed-files.title"),
            link: RouteEnum.PARSED_FILES,
            Icon: SubtitlesOutlined,
            count: (counts.parsing_counter ?? 0),
            requiresSubscription: true,
            preload: preloadParsedFiles,
          },
          {
            label: "HP Finder",
            link: RouteEnum.PARSED_FILES_VIEW_VALIDATION,
            Icon: FunctionsRoundedIcon,
            count: counts.validated_counter,
            requiresSubscription: true,
          },
          {
            label: t("files-history.title"),
            link: RouteEnum.PARSED_FILES_VIEW_HISTORY,
            Icon: StorageRounded,
            count: counts.history_counter,
            requiresSubscription: true,
          },
          {
            label: t("customers.title"),
            link: RouteEnum.CUSTOMERS,
            Icon: PeopleAltRounded,
            requiresSubscription: true,
          },
          {
            label: t("config.title"),
            link: RouteEnum.CONFIG,
            Icon: TuneRounded,
            requiresSubscription: true,
          },
        ],
    [t, counts, isAdmin, isNewUser, isViewer, isAuditor, archiveLink, auditLink]
  );

  const supportLink = useMemo(
    () => ({
      label: t("support.title"),
      Icon: Help,
      isButton: true,
      onClick: () => setSupportPopupOpen(true),
      requiresSubscription: false,
    }),
    [t]
  );

  const filteredLinks = useMemo(() => {
    let links;

    if (isAuditor) {
      links = [auditLink];
    } else if (isViewer) {
      links = [archiveLink];
    } else {
      const alwaysShowLinks =
        isNewUser || isAdmin
          ? sidebarLinks.filter(
              (link) =>
                link.link === RouteEnum.COMPANY || link.link === RouteEnum.AUDIT
            )
          : [];

      if (isLoadingSubscription) {
        links = [
          ...alwaysShowLinks,
          ...sidebarLinks.filter(
            (link) =>
              !link.requiresSubscription &&
              link.link !== RouteEnum.COMPANY &&
              link.link !== RouteEnum.AUDIT
          ),
        ];
      } else if (hasActiveSubscription) {
        links = sidebarLinks;
      } else {
        links = [
          ...alwaysShowLinks,
          ...sidebarLinks.filter(
            (link) =>
              !link.requiresSubscription &&
              link.link !== RouteEnum.COMPANY &&
              link.link !== RouteEnum.AUDIT
          ),
        ];
      }
    }

    return links;
  }, [
    sidebarLinks,
    hasActiveSubscription,
    isLoadingSubscription,
    isNewUser,
    isAdmin,
    isViewer,
    isAuditor,
    archiveLink,
    auditLink,
  ]);

  if (!isAuthorised) return null;

  return (
    <Drawer variant="permanent" open={sidebar.opened}>
      <DrawerHeader sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: sidebar.opened ? 1 : 0,
              minHeight: 40,
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: sidebar.opened ? "rotate(360deg)" : "rotate(0deg)",
              }}
            >
              <img
                src="/img/Logo-HSE-Genius.png"
                width={24}
                height={24}
                style={{
                  objectFit: "contain",
                  flexShrink: 0,
                }}
                alt="Logo"
              />
            </Box>
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: "inherit",
                whiteSpace: "nowrap",
                overflow: "hidden",
                maxWidth: sidebar.opened ? 120 : 0,
                minWidth: sidebar.opened ? undefined : 0,
                opacity: sidebar.opened ? 1 : 0,
                transition: "opacity 0.35s ease, max-width 0.35s ease",
              }}
            >
              HSE Genius
              <Box
                component="span"
                sx={{
                  fontWeight: 400,
                  fontSize: "0.65em",
                  verticalAlign: "super",
                }}
              >
                ®
              </Box>
            </Typography>
          </Box>
          {isAuthorised && (
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              onClick={() => sidebar.toggle()}
              sx={{
                "&:hover": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <ChevronLeft sx={{
                fontSize: 22,
                color: iconColor(theme),
                transform: sidebar.opened ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.3s ease",
              }} />
            </IconButton>
          )}
        </Box>
      </DrawerHeader>

      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <SidebarLinks links={filteredLinks} />
        </Box>

        {/* ─── Footer icons — aligned with page footer ─── */}
        <Box
          onMouseEnter={() => setFooterHovered(true)}
          onMouseLeave={() => setFooterHovered(false)}
          sx={{
            position: "relative",
            height: `${FOOTER_HEIGHT}px`,
            boxSizing: "border-box",
            borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: "row",
            justifyContent: sidebar.opened ? "space-evenly" : "center",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {isAuthorised && (
            <>
              {sidebar.opened ? (
                <>
                  <ThemeSwitcher />
                  <Tooltip title={t("footer.instructions")}>
                    <IconButton
                      component="a"
                      href="https://hsegenius.com/instructions.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="inherit"
                      size="small"
                      sx={{ padding: "4px", "&:hover": { backgroundColor: "transparent" } }}
                    >
                      <MenuBook sx={{ fontSize: 20, color: iconColor(theme) }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("support.title")}>
                    <IconButton
                      onClick={() => setSupportPopupOpen(true)}
                      color="inherit"
                      size="small"
                      sx={{ padding: "4px", "&:hover": { backgroundColor: "transparent" } }}
                    >
                      <Help sx={{ fontSize: 20, color: iconColor(theme) }} />
                    </IconButton>
                  </Tooltip>
                  <UserMenu tooltipTitle={t("profile.title")} />
                </>
              ) : (
                <>
                  {/* Ponte invisibile: mantiene l'hover attivo spostandosi verso le icone */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: "100%",
                      height: 100,
                      pointerEvents: "auto",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      left: "50%",
                      bottom: "100%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.25,
                      mb: 0.5,
                      opacity: footerHovered ? 1 : 0,
                      pointerEvents: footerHovered ? "auto" : "none",
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <ThemeSwitcher
                      tooltipPlacement="right"
                      tooltipSlotProps={SIDEBAR_CLOSED_TOOLTIP_SLOT_PROPS}
                    />
                    <Tooltip
                      title={t("footer.instructions")}
                      placement="right"
                      slotProps={SIDEBAR_CLOSED_TOOLTIP_SLOT_PROPS}
                    >
                      <IconButton
                        component="a"
                        href="https://hsegenius.com/instructions.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                        size="small"
                        sx={{ padding: "4px", "&:hover": { backgroundColor: "transparent" } }}
                      >
                        <MenuBook sx={{ fontSize: 20, color: iconColor(theme) }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={t("support.title")}
                      placement="right"
                      slotProps={SIDEBAR_CLOSED_TOOLTIP_SLOT_PROPS}
                    >
                      <IconButton
                        onClick={() => setSupportPopupOpen(true)}
                        color="inherit"
                        size="small"
                        sx={{ padding: "4px", "&:hover": { backgroundColor: "transparent" } }}
                      >
                        <Help sx={{ fontSize: 20, color: iconColor(theme) }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <UserMenu
                    tooltipTitle={t("profile.title")}
                    tooltipPlacement="right"
                    tooltipSlotProps={SIDEBAR_CLOSED_TOOLTIP_SLOT_PROPS}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Box>
      <SupportPopup open={supportPopupOpen} onClose={() => setSupportPopupOpen(false)} />
    </Drawer>
  );
};

export default Sidebar;
